import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import HandshakeIcon from "@mui/icons-material/Handshake";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useBKGPDVCompanyConnections } from "../hooks";
import type {
  PDVCompanyConnections as PDVCompanyConnectionsResult,
  PDVStrengthDefinition,
} from "../types";

interface PDVCompanyConnectionsProps {
  workspaceId: string;
  companyRef: string;
}

const pdvErrorMessages: Record<string, string> = {
  pdv_credential_not_deposited:
    "SSI connections have not been provisioned for this environment yet.",
  pdv_not_configured:
    "SSI connections are not configured for this environment.",
  pdv_credential_rejected:
    "SSI connections are misconfigured for this environment.",
  pdv_scope_insufficient:
    "SSI connections are misconfigured for this environment.",
  pdv_verdict_not_understood:
    "SSI connections could not interpret PDV's response for this company.",
  pdv_verdict_candidate_mismatch: "PDV returned an inconsistent company match.",
  pdv_insufficient_permissions:
    "You do not have permission to view SSI connections.",
  pdv_workspace_not_found:
    "SSI connections are not available for this workspace.",
  pdv_organization_not_allowlisted:
    "SSI connections are not available for this organization.",
  pdv_integration_not_registered:
    "SSI connections are not configured for this environment.",
  pdv_integration_not_enabled_for_organization:
    "SSI connections are not enabled for this organization.",
  pdv_integration_not_enabled_for_workspace:
    "SSI connections are not enabled for this workspace.",
  pdv_disabled: "SSI connections are switched off in this environment.",
  pdv_environment_not_permitted:
    "SSI connections are not available in this environment.",
  pdv_environment_unidentified:
    "SSI connections are not available in this environment.",
  pdv_environment_forbidden_site:
    "SSI connections are not available in this environment.",
  pdv_environment_identity_mismatch:
    "SSI connections are not available in this environment.",
  pdv_environment_attribution_mismatch:
    "SSI connections are misconfigured for this environment.",
};

const messageForPDVError = (error: unknown): string => {
  if (!error || typeof error !== "object") {
    return "SSI connections are temporarily unavailable.";
  }

  const response = (error as { response?: Record<string, unknown> }).response;
  const detail = response?.detail;
  if (detail && typeof detail === "object") {
    const code = (detail as { code?: unknown }).code;
    if (typeof code === "string" && pdvErrorMessages[code]) {
      return pdvErrorMessages[code];
    }
  }

  if ((error as { status?: unknown }).status === 404) {
    return "This company is no longer available.";
  }
  if ((error as { status?: unknown }).status === 422) {
    return "This company does not have enough identity data for an SSI lookup.";
  }
  return "SSI connections are temporarily unavailable.";
};

type StrengthLabel = "weak" | "medium" | "strong";

const strengthChips: Record<
  StrengthLabel,
  { text: string; color: "success" | "primary" | "neutral" }
> = {
  strong: { text: "Strong", color: "success" },
  medium: { text: "Medium", color: "primary" },
  weak: { text: "Weak", color: "neutral" },
};

// Shown once, beside the card title. Every word of the explanation is Personal
// Data Vault's, relayed by the backend: PDV owns the bands and computes the
// example figures from its scoring weights, so nothing is restated here.
const StrengthExplanation: React.FC<{ definition: PDVStrengthDefinition }> = ({
  definition,
}) => (
  <Tooltip
    variant="outlined"
    arrow
    placement="bottom-end"
    title={
      <Box sx={{ maxWidth: 320, p: 0.5 }}>
        <Typography level="title-sm">How strength is measured</Typography>
        <Typography level="body-xs" sx={{ mt: 0.5 }}>
          {definition.summary}
        </Typography>
        {definition.bands.map((band) => (
          <Typography key={band.label} level="body-xs" sx={{ mt: 0.5 }}>
            <b>{strengthChips[band.label]?.text ?? band.label}</b> ({band.range}
            ){band.example ? `: ${band.example}` : ""}
          </Typography>
        ))}
      </Box>
    }
  >
    <IconButton
      size="sm"
      variant="plain"
      color="neutral"
      aria-label="How strength is measured"
    >
      <InfoOutlinedIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

const relationshipText = (values: string[]) =>
  values.map((value) => value.toLowerCase().replace(/_/g, " ")).join(" · ");

const connectionCountText = (count: number) =>
  count === 1 ? "1 SSI connection" : `${count} SSI connections`;

const PDVCompanyConnections: React.FC<PDVCompanyConnectionsProps> = ({
  workspaceId,
  companyRef,
}) => {
  const fetchConnections = useBKGPDVCompanyConnections();
  const [result, setResult] = useState<PDVCompanyConnectionsResult | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setResult(null);
    setErrorMessage(null);

    fetchConnections(workspaceId, companyRef)
      .then((data) => {
        if (active) setResult(data);
      })
      .catch((error: unknown) => {
        if (active) setErrorMessage(messageForPDVError(error));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [companyRef, fetchConnections, workspaceId]);

  const resolvedResult =
    result?.match_status === "exact" || result?.match_status === "parent_domain"
      ? result
      : null;
  const hasStrength =
    resolvedResult?.connections.some(
      (connection) =>
        !!connection.strength_label &&
        connection.strength_label in strengthChips,
    ) === true;
  const definition = resolvedResult?.strength_definition;
  const strengthDefinition =
    definition && definition.summary && definition.bands?.length
      ? definition
      : null;
  const matchedCompany = [result?.company_name, result?.company_domain]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card variant="outlined" data-testid="pdv-company-connections">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <HandshakeIcon color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography level="h3">SSI Connections</Typography>
              <Typography level="body-xs" color="neutral">
                Live relationship data from Personal Data Vault
              </Typography>
            </Box>
            {!loading && !errorMessage && hasStrength && strengthDefinition && (
              <StrengthExplanation definition={strengthDefinition} />
            )}
          </Stack>

          {loading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size="sm" />
              <Typography level="body-sm">Checking SSI connections…</Typography>
            </Stack>
          )}

          {!loading && errorMessage && (
            <Alert color="neutral" variant="soft">
              {errorMessage}
            </Alert>
          )}

          {!loading &&
            !errorMessage &&
            result?.match_status === "ambiguous" && (
              <Alert color="neutral" variant="soft">
                This company could not be matched reliably, so no relationship
                data is shown.
              </Alert>
            )}

          {!loading && !errorMessage && result?.match_status === "none" && (
            <Typography level="body-sm" color="neutral">
              No reliable match was found in PDV.
            </Typography>
          )}

          {!loading &&
            !errorMessage &&
            result?.match_status === "parent_domain" && (
              <Alert color="warning" variant="soft">
                PDV matched this company using its parent domain
                {matchedCompany ? `: ${matchedCompany}` : ""}.
              </Alert>
            )}

          {!loading &&
            !errorMessage &&
            result?.match_status === "exact" &&
            matchedCompany && (
              <Typography level="body-xs" color="neutral">
                Matched in PDV: {matchedCompany}
              </Typography>
            )}

          {!loading &&
            !errorMessage &&
            resolvedResult &&
            resolvedResult.connections.length === 0 && (
              <Typography level="body-sm" color="neutral">
                No known SSI connections were found for this company.
              </Typography>
            )}

          {!loading &&
            !errorMessage &&
            resolvedResult &&
            resolvedResult.connections.length > 0 && (
              // Collapsed by default: a large company can have hundreds of
              // pairs, so the card shows only the total until it is opened.
              <AccordionGroup variant="plain" disableDivider>
                <Accordion data-testid="pdv-connections-accordion">
                  <AccordionSummary>
                    <Typography level="title-sm">
                      {connectionCountText(resolvedResult.connections.length)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      {resolvedResult.connections.map((connection, index) => (
                        <React.Fragment
                          key={`${connection.ssi_employee_name}-${connection.contact_name}-${index}`}
                        >
                          {index > 0 && <Divider />}
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Box>
                              <Typography level="title-sm">
                                {connection.ssi_employee_name} knows{" "}
                                {connection.contact_name}
                              </Typography>
                              {connection.contact_job_title && (
                                <Typography level="body-sm" color="neutral">
                                  {connection.contact_job_title}
                                </Typography>
                              )}
                              {connection.relationship_types.length > 0 && (
                                <Typography level="body-xs" color="neutral">
                                  {relationshipText(
                                    connection.relationship_types,
                                  )}
                                </Typography>
                              )}
                            </Box>
                            {connection.strength_label &&
                              strengthChips[connection.strength_label] && (
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  color={
                                    strengthChips[connection.strength_label]
                                      .color
                                  }
                                >
                                  {
                                    strengthChips[connection.strength_label]
                                      .text
                                  }
                                </Chip>
                              )}
                          </Stack>
                        </React.Fragment>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </AccordionGroup>
            )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PDVCompanyConnections;
